import React, { useEffect } from "react";
import { connect } from "react-redux";
import { setOpenDialog } from "../BetHistory/betHistorySlice";
import { useDispatch } from "react-redux";
import {
  getDateTimeFormate,
  fixedDigit,
} from "../../sharedfiles/helper";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { ChevronRight } from "lucide-react";
import Tooltips from "../Common/Tooltip";
import { betIdHeaderInfo, betIdHeaders } from "../Common/data/BettingHistory/BettinghistoryData";
import DownloadWidget from "../Common/DownloadWidget";
import { cn } from "../../lib/utils";
import { useColumnPreferences } from "../../sharedfiles/hooks/useColumnPreferences";
import TableToolbar from "../Common/table/TableToolbar";

// --- Bet Builder support ----------------------------------------------------
// A selection from the `betSelections` API is a Bet Builder when `isBetBuilder`
// is true; its individual legs are in the `selections` array. Each leg carries
// betSelectionId / marketName / selectionName / betSelectionStatus (no per-leg
// odds or fixture).
const isBetBuilderSelection = (item) =>
  item?.isBetBuilder === true || item?.betBuilder === true;

const getBetBuilderItems = (item) => item?.selections || [];

// Columns shown for each Bet Builder leg in the expanded sub-table.
const betBuilderItemHeaders = [
  "Bet Selection ID",
  "Market Name",
  "Selection Name",
  "Selection Status",
];

const buildBetBuilderItemCells = (leg) => [
  leg?.betSelectionId ?? "-",
  leg?.marketName ?? "-",
  leg?.selectionName ?? "-",
  leg?.betSelectionStatus ?? "-",
];

// A single display row for a selection - kept in one place so the on-screen
// table and the download export always stay in sync.
const buildSelectionCells = (item) => [
  item.betSelectionId,
  item.fixtureName,
  getDateTimeFormate(item.fixtureStartDate),
  item.fixtureStatus,
  item.leagueName,
  item.marketName,
  fixedDigit(item.odds, 2),
  item.sportName,
  item.selectionName,
  item.betSelectionStatus,
  item.score || "-",
  item.boostedOddsValue,
  item.isBoostedOdds ? "True" : "False",
];

const BetId = (props) => {
  const { openDialog, setBack, potentialPayout, onClose, enableDownload = false, pdfTitle = "Bet Selections", fileName = "bet-selections", ...rest } = props;
  const dispatch = useDispatch();
  const [tableData, setTableData] = React.useState([]);
  const [expandedRows, setExpandedRows] = React.useState({});

  const selections =
    Array.isArray(props.data) && props.data.length > 0 ? props.data : [];

  const onDialogClose = () => {
    if (onClose) {
      onClose();
    } else {
      setBack && dispatch(setBack([]));
      dispatch(setOpenDialog(false));
    }
  };

  const toggleRow = (key) => {
    setExpandedRows((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const updateDataToDialog = () => {
    const tBody = selections.map((item) => buildSelectionCells(item));
    setTableData([betIdHeaders, betIdHeaderInfo, ...tBody]);
  };

  useEffect(() => {
    let timer = null;
    setExpandedRows({});
    if (props.data.length > 0) {
      updateDataToDialog();
    } else {
      timer = setTimeout(() => setTableData([]), 100);
    }

    return () => clearTimeout(timer);
  }, [props.data]);

  const { visibleIndices, allColumns, toggleVisibility, reorder, resetToDefault } =
    useColumnPreferences("betIdBetId", betIdHeaders);

  const totalColumns = visibleIndices.length + 1; // +1 for the expand toggle column

  return (
    <>
      <Dialog open={openDialog} onOpenChange={() => onDialogClose()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex flex-row items-center justify-between space-y-0 pr-14">
            <DialogTitle className="text-lg font-semibold">Bet Selections & Details</DialogTitle>
            {enableDownload && <DownloadWidget tableData={tableData} fileName={fileName} pdfTitle={pdfTitle} />}
          </DialogHeader>
          <div className='space-y-4 overflow-y-auto flex-1 min-h-0'>
            <div className="relative rounded-lg border bg-card shadow-sm">
              <TableToolbar
                columns={allColumns}
                onToggle={toggleVisibility}
                onReorder={reorder}
                onReset={resetToDefault}
              />
              <Table scrollKey="betIdSelections">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10" />
                    {visibleIndices.map((origIdx) => (
                      <TableHead
                        key={betIdHeaders[origIdx]}
                        className={cn("whitespace-nowrap", origIdx === 0 ? "text-left" : "text-center")}
                      >
                        <div className={cn("flex items-center gap-2", origIdx === 0 ? "justify-start" : "justify-center")}>
                          <span className="font-semibold">{betIdHeaders[origIdx]}</span>
                          {betIdHeaderInfo[origIdx] && (
                            <Tooltips info={`${betIdHeaderInfo[origIdx]}`} placement="left" />
                          )}
                        </div>
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selections.length > 0 ? (
                    selections.map((item, index) => {
                      const cells = buildSelectionCells(item);
                      const isBetBuilder = isBetBuilderSelection(item);
                      const legs = isBetBuilder ? getBetBuilderItems(item) : [];
                      const hasLegs = Array.isArray(legs) && legs.length > 0;
                      const isExpanded = !!expandedRows[index];

                      return (
                        <React.Fragment key={index}>
                          <TableRow
                            className={cn(
                              "hover:bg-muted/30",
                              isBetBuilder && hasLegs && "cursor-pointer"
                            )}
                            onClick={
                              isBetBuilder && hasLegs ? () => toggleRow(index) : undefined
                            }
                          >
                            <TableCell className="text-center">
                              {isBetBuilder && hasLegs && (
                                <button
                                  type="button"
                                  aria-label={isExpanded ? "Collapse bet builder" : "Expand bet builder"}
                                  aria-expanded={isExpanded}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleRow(index);
                                  }}
                                  className="inline-flex items-center justify-center text-muted-foreground hover:text-foreground"
                                >
                                  <ChevronRight
                                    className={cn(
                                      "h-4 w-4 transition-transform duration-300 ease-in-out",
                                      isExpanded && "rotate-90"
                                    )}
                                  />
                                </button>
                              )}
                            </TableCell>
                            {visibleIndices.map((origIdx) => (
                              <TableCell
                                key={origIdx}
                                className={origIdx === 0 ? "text-left" : "text-center"}
                              >
                                {origIdx === 0 && isBetBuilder ? (
                                  <div className="flex flex-col items-start gap-1">
                                    <span>{cells[origIdx]}</span>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300">
                                      Bet Builder
                                    </span>
                                  </div>
                                ) : (
                                  cells[origIdx]
                                )}
                              </TableCell>
                            ))}
                          </TableRow>

                          {isBetBuilder && hasLegs && (
                            <TableRow className="hover:bg-transparent border-b-0">
                              <TableCell colSpan={totalColumns} className="p-0">
                                <div
                                  className={cn(
                                    "grid transition-all duration-300 ease-in-out",
                                    isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                                  )}
                                >
                                  <div className="overflow-hidden">
                                    <div className="bg-muted/40 p-4">
                                <div className="rounded-md border bg-card overflow-x-auto w-fit max-w-full">
                                  <Table className="w-auto">
                                    <TableHeader>
                                      <TableRow>
                                        {betBuilderItemHeaders.map((header) => (
                                          <TableHead
                                            key={header}
                                            className="text-left whitespace-nowrap font-semibold py-2"
                                          >
                                            {header}
                                          </TableHead>
                                        ))}
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {legs.map((leg, legIndex) => (
                                        <TableRow key={legIndex} className="hover:bg-muted/30">
                                          {buildBetBuilderItemCells(leg).map((legCell, legCellIndex) => (
                                            <TableCell
                                              key={legCellIndex}
                                              className="text-left whitespace-nowrap py-2"
                                            >
                                              {legCell}
                                            </TableCell>
                                          ))}
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                    </div>
                                    </div>
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell colSpan={totalColumns} className="text-center py-8 text-muted-foreground">
                        No Data Found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            <div className="w-1/3 ml-350">
              <div className="CMS-alert alert-primary">
                <div className="flex flex-wrap -mx-3">
                  <div className="w-1/2 px-3 vh_laign_center alert_conetnet">
                    Potential Payout
                  </div>
                  <div className="w-1/2 px-3 vh_laign_center">
                    <b>{potentialPayout}</b>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

function mapStateToProps(state) {
  return {
    openDialog: state.betHistorySliceReducer.openDialog,
  };
}
export default connect(mapStateToProps)(BetId);
